export * from './di.decorators';
import { ParameterMap, IChildContainerInjection, getOwnMetadata } from './di.decorators';

interface IConstructorMetadata<T> {
    parameterMap: ParameterMap<T>;
    target: Function | any;
}

interface IParentParameterResolvement<T> {
    parentParameterIndex?: number;
    target?: T;
}

interface IChildWithParent {
    target: any;
    parentParameterIndex: number;
}

export class Container<T> {
    private constructorMetadataMap = new Map<T | Function, IConstructorMetadata<T>>();
    private childContainerInjectionMap = new Map<T | Function, IChildContainerInjection>();
    private objectMap = new Map<T | Function, any>();
    private parentInjectionMap = new Map<T | Function, number>();
    private factoryMap = new Map<T | Function, (...args: any[]) => any>();

    constructor(private parent?: Container<T>) { }

    register(token: T, target: any): void {
        const parameterMap = getOwnMetadata('bf-di:parameters', target) as ParameterMap<T>;
        const childContainerInjection = getOwnMetadata('bf-di:child-container-injection-parameter-index', target) as IChildContainerInjection;
        const parentParameterIndex = getOwnMetadata('bf-di:parent-parameter-index', target) as number;
        if (target instanceof Function) {
            if (childContainerInjection) {
                this.childContainerInjectionMap.set(token, childContainerInjection);
            }
            if (typeof parentParameterIndex === 'number') {
                this.parentInjectionMap.set(token, parentParameterIndex);
            }
            if (parameterMap) {
                this.constructorMetadataMap.set(token, { parameterMap, target });
            }
            else {
                this.constructorMetadataMap.set(token, { parameterMap: new Map(), target });
            }
        }
        else {
            this.objectMap.set(token, target);
        }
    }

    unregister(token: T): void {
        this.constructorMetadataMap.delete(token);
        this.childContainerInjectionMap.delete(token);
        this.objectMap.delete(token);
        this.factoryMap.delete(token);
    }

    tryResolve<R>(token: T): R | undefined {
        const { target } = this.resolveWithParent<R>(token, false);
        return target;
    }

    resolve<R>(token: T): R {
        const { target } = this.resolveWithParent<R>(token, false);
        if (!target) {
            throw new Error(`Could not resolve token '${token}'.`);
        }
        return target;
    }

    registerFactory<R>(token: T, context: (container: Container<T>) => R): void {
        this.factoryMap.set(token, context);
    }

    private resolveWithParent<T2>(token: T, isOptional: boolean): IParentParameterResolvement<T2> {
        const metadata = this.recursivelyGetConstructorMetadata(token);
        const childContainerInjection = this.childContainerInjectionMap.get(token);
        const parentParameterIndex = this.parentInjectionMap.get(token);
        const args: any[] = [];
        if (metadata) {
            if (typeof metadata.target === 'function') {
                const childrenWithParent: IChildWithParent[] = [];
                if (metadata.parameterMap) {
                    for (const [index, parameter] of metadata.parameterMap.entries()) {
                        const { target, parentParameterIndex } = this.resolveWithParent(parameter.token, parameter.optional || isOptional);
                        if (!target) {
                            if (!isOptional && !parameter.optional) {
                                throw new Error(`Could not resolve symbol '${parameter.token}'.`);
                            }
                            continue;
                        }
                        args[index] = target;
                        if (parentParameterIndex !== undefined) {
                            childrenWithParent.push({
                                target,
                                parentParameterIndex,
                            });
                        }
                    }
                }
                this.applyChildInjectionArgument(childContainerInjection, args);
                const instance = new metadata.target(...args);

                for (const child of childrenWithParent) {
                    let enumKeyIndex = 0;
                    for (const key in child.target) {
                        if (enumKeyIndex === child.parentParameterIndex) {
                            child.target[key] = instance;
                        }
                        enumKeyIndex++;
                    }
                }
                return { target: instance, parentParameterIndex };
            }
        }
        const object = this.objectMap.get(token);
        if (object) {
            return { target: object };
        }
        const factory = this.factoryMap.get(token);
        if (factory) {
            return { target: factory(this) };
        }
        return { target: undefined };
    }

    private applyChildInjectionArgument(childInjection: IChildContainerInjection | undefined, args: any[]): void {
        if (childInjection) {
            const childContainer = new Container(this);
            args[childInjection.parameterIndex] = childContainer;
        }
    }

    private recursivelyGetConstructorMetadata(token: T): IConstructorMetadata<T> | undefined {
        let currentContainer: Container<T> | undefined = this;
        while (currentContainer) {
            const metadata = currentContainer.constructorMetadataMap.get(token);
            if (metadata) {
                return metadata;
            }
            currentContainer = currentContainer.parent;
        }
        return undefined;
    }
}
